import { ArrowLeft, Info } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPhoneDisplay } from '../../utils/whatsapp';
import ChatThread from './ChatThread';
import Composer from './Composer';
import ContactAvatar from './ContactAvatar';
import TemplateSendPanel from './TemplateSendPanel';

/**
 * The middle pane: a WhatsApp-style header, the wallpaper thread and the composer bar. `phone` null
 * means nothing is selected, which renders the wide-screen placeholder instead.
 * @param {{phone: string | null, contact: any, wholesalePartner?: {id: string, name: string, stage?: string} | null, messages: any[], windowOpen: boolean, suppressed: boolean, sendDisabled: boolean, loading: boolean, error: string, onRetry: () => void, hasMoreMessages: boolean, loadingEarlier: boolean, onLoadEarlier: () => void, onSend: (text: string, replyToMessageId?: string) => Promise<any>, onSendMedia?: (media: any, forPhone?: string) => Promise<any>, onSendReaction?: (targetMessageId: string, emoji: string) => Promise<any>, onSendInteractive?: (payload: any) => Promise<any>, onSendCatalog?: (body: string) => Promise<any>, onSendTemplate?: (payload: {template_id: string, body_params: string[]}) => Promise<any>, onEditMessage?: (messageId: string, text: string) => Promise<any>, onDeleteMessage?: (messageId: string) => Promise<any>, onFetchMedia?: (messageId: string) => Promise<{url: string, mime_type: string}>, autoOpenTemplates?: boolean, startedName?: string, onBack: () => void, onToggleContact: () => void, onOpenNewCampaign?: () => void}} props
 */
const ConversationThread = ({
  phone,
  contact,
  wholesalePartner,
  messages,
  windowOpen,
  suppressed,
  sendDisabled,
  loading,
  error,
  onRetry,
  hasMoreMessages,
  loadingEarlier,
  onLoadEarlier,
  onSend,
  onSendMedia,
  onSendReaction,
  onSendInteractive,
  onSendCatalog,
  onSendTemplate,
  onEditMessage,
  onDeleteMessage,
  onFetchMedia,
  autoOpenTemplates = false,
  startedName = '',
  onBack,
  onToggleContact,
}) => {
  const [templatesRequested, setTemplatesRequested] = useState(autoOpenTemplates);
  // {id, text} of the message being quoted, or null. Cleared on a successful send, or whenever
  // the conversation changes (ConversationThread remounts per phone - see InboxTab's `key`).
  const [replyingTo, setReplyingTo] = useState(null);
  // {id, text} of the message being edited, or null. Mutually exclusive with replyingTo - editing
  // a draft replaces the composer's contents outright, it doesn't compose a new quoted reply.
  const [editingMessage, setEditingMessage] = useState(null);
  if (!phone) {
    return (
      <div className="scheme-light flex h-full flex-col items-center justify-center gap-2 bg-[#F0F2F5] text-center text-gray-500">
        <p className="text-sm">Select a conversation to start reading.</p>
      </div>
    );
  }

  const title = contact?.name || startedName || formatPhoneDisplay(phone);
  // `onOpenNewCampaign` is still accepted (see props) but the closed-window bar opens the panel now.
  const panelOpen =
    templatesRequested && !loading && !error && !windowOpen && !suppressed && !sendDisabled && !!onSendTemplate;

  const handleSend = async (text) => {
    const target = replyingTo;
    const result = await onSend(text, target?.id);
    if (result.ok && target) setReplyingTo(null);
    return result;
  };

  const handleSendMedia = onSendMedia
    ? async (body, forPhone) => {
        const target = replyingTo;
        const result = await onSendMedia({ ...body, reply_to_message_id: target?.id }, forPhone);
        if (result.ok && target) setReplyingTo(null);
        return result;
      }
    : undefined;

  const handleSaveEdit = onEditMessage
    ? async (text) => {
        const target = editingMessage;
        const result = await onEditMessage(target.id, text);
        if (result.ok) setEditingMessage(null);
        return result;
      }
    : undefined;

  const handleDeleteMessage = onDeleteMessage
    ? (messageId) => {
        if (
          !window.confirm(
            'Delete this message? This only removes it from your own view - WhatsApp has no way to unsend it from the customer.',
          )
        ) {
          return;
        }
        if (editingMessage?.id === messageId) setEditingMessage(null);
        if (replyingTo?.id === messageId) setReplyingTo(null);
        onDeleteMessage(messageId);
      }
    : undefined;

  return (
    <div className="scheme-light flex h-full min-w-0 flex-1 flex-col text-gray-900">
      <div className="flex items-center gap-3 bg-[#008069] px-3 py-2 text-white">
        <button type="button" onClick={onBack} aria-label="Back to conversations" className="lg:hidden">
          <ArrowLeft size={20} />
        </button>
        <ContactAvatar name={contact?.name} phone={phone} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="truncate text-xs text-white/80">{formatPhoneDisplay(phone)}</p>
          {wholesalePartner?.id && (
            <Link
              to={`/wholesale/${wholesalePartner.id}`}
              className="mt-0.5 inline-block max-w-full truncate rounded bg-white/15 px-2 py-0.5 text-xs text-white hover:bg-white/25"
            >
              Wholesale partner: {wholesalePartner.name}
            </Link>
          )}
        </div>
        <button type="button" onClick={onToggleContact} aria-label="Contact info" className="shrink-0 p-1">
          <Info size={20} />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {loading ? (
          <div className="flex flex-1 items-center justify-center bg-[#EFEAE2]">
            <p className="text-sm text-gray-500">Loading conversation...</p>
          </div>
        ) : error ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[#EFEAE2] p-6 text-center">
            <p className="text-sm text-gray-700">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {hasMoreMessages && (
              <div className="flex justify-center bg-[#EFEAE2] py-2">
                <button
                  type="button"
                  onClick={onLoadEarlier}
                  disabled={loadingEarlier}
                  className="text-xs text-[#008069] underline disabled:opacity-50"
                >
                  {loadingEarlier ? 'Loading...' : 'Load earlier messages'}
                </button>
              </div>
            )}
            <ChatThread
              messages={messages}
              className="flex-1"
              label={`Conversation with ${title}`}
              onFetchMedia={onFetchMedia}
              onReply={(message) => {
                setEditingMessage(null);
                setReplyingTo({ id: message.id, text: message.text });
              }}
              onReact={onSendReaction}
              onEdit={
                onEditMessage
                  ? (message) => {
                      setReplyingTo(null);
                      setEditingMessage({ id: message.id, text: message.edit_text || '' });
                    }
                  : undefined
              }
              onDelete={handleDeleteMessage}
            />
          </>
        )}

        {panelOpen ? (
          <TemplateSendPanel
            contactName={contact?.name || startedName}
            onSend={onSendTemplate}
            onCancel={() => setTemplatesRequested(false)}
          />
        ) : (
          <Composer
            phone={phone}
            windowOpen={windowOpen}
            suppressed={suppressed}
            sendDisabled={sendDisabled}
            onSend={handleSend}
            onSendMedia={handleSendMedia}
            onSendInteractive={onSendInteractive}
            onSendCatalog={onSendCatalog}
            onOpenTemplates={() => setTemplatesRequested(true)}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            editing={editingMessage}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={() => setEditingMessage(null)}
          />
        )}
      </div>
      <p className="border-t border-gray-100 bg-white px-3 py-1 text-center text-[11px] text-gray-400">
        Messages typed on your phone may not appear here.
      </p>
    </div>
  );
};

export default ConversationThread;
